// Entry table columns are dynamic: one per column of the parent data source, so
// the schema drives the header row. Each header pairs the column's TYPE GLYPH
// with its name — the same glyph the Add-column picker shows for that type,
// read from the shared columnTypes map — and each cell reads the entry's value
// for that column, dashing when unset. A trailing kebab closes the row.
//
// Nothing is sortable: POST data-source-entries/all takes no orderBy, and
// DataTable draws a chevron for any column marked sortable whether or not it can
// act — so declaring one here would ship a dead affordance. The design draws no
// chevrons either.
//
// Every DATA column's header carries a checkbox, so columns can be selected as
// an axis of their own. That state lives in the page (it is mutually exclusive
// with row selection), not here; this file only renders the control — and only
// when the page says so (`selectableColumns`): without the verbs behind the
// column bar's actions, a header checkbox would select into a bar offering
// nothing. The kebab callbacks are verb-gated by the page: absent = hidden.

import { Checkbox, Icon, type DataTableColumn } from '@/components/common';
import type { DataSourceColumn } from '@/api/endpoints/datasourceApi';
import type { DataSourceEntryListItem } from '@/api/endpoints/datasourceEntriesApi';
import { columnTypeIcon } from '../shared/columnTypes';
import DataSourceEntryRowActions from './DataSourceEntryRowActions';

interface UseDataSourceEntryColumnsArgs {
  columns: DataSourceColumn[];
  /** Column ids currently ticked — the page owns the set. */
  selectedColumns: Set<string>;
  onToggleColumn: (columnId: string) => void;
  /** Render the header checkboxes at all — the page passes false when the
   *  user holds neither of the column bar's verbs (edit/delete). */
  selectableColumns: boolean;
  /** Row kebab — each handler receives the row it was opened from. Absent =
   *  hidden in the kebab. */
  onEdit?: (entry: DataSourceEntryListItem) => void;
  onExport?: (entry: DataSourceEntryListItem) => void;
  onDelete?: (entry: DataSourceEntryListItem) => void;
}

export const useDataSourceEntryColumns = ({
  columns,
  selectedColumns,
  onToggleColumn,
  selectableColumns,
  onEdit,
  onExport,
  onDelete,
}: UseDataSourceEntryColumnsArgs): DataTableColumn<DataSourceEntryListItem>[] => [
  ...columns.map<DataTableColumn<DataSourceEntryListItem>>((column) => ({
    key: column.columnId,
    label: (
      <span className='datasource-table__col-head'>
        {/* The visible name sits beside the box rather than inside its label,
            so the checkbox needs its own accessible name. */}
        {selectableColumns && (
          <Checkbox
            className='datasource-table__col-check'
            size='sm'
            checked={selectedColumns.has(column.columnId)}
            aria-label={column.columnName}
            onChange={() => onToggleColumn(column.columnId)}
          />
        )}
        <Icon
          name={columnTypeIcon(column.dataType)}
          size={15}
          className='datasource-table__col-glyph'
          aria-hidden='true'
        />
        {column.columnName}
      </span>
    ),
    width: 'minmax(10rem, 1fr)',
    render: (entry) =>
      entry.values[column.columnId] ? (
        <span className='datasource-table__value'>{entry.values[column.columnId]}</span>
      ) : (
        <span className='datasource-table__dash'>—</span>
      ),
  })),
  {
    key: 'actions',
    label: '',
    width: '3.5rem',
    align: 'end',
    render: (entry) => (
      <DataSourceEntryRowActions
        onEdit={onEdit && (() => onEdit(entry))}
        onExport={onExport && (() => onExport(entry))}
        onDelete={onDelete && (() => onDelete(entry))}
      />
    ),
  },
];
